import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  SecretsBodyResponse,
  UpdateSecretsBody,
  SecretsManagerClientApi,
  UpdateSecretsResponse,
  RevertSecretVersionBody,
} from './types';

export interface SecretsManagerClientOptions {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
}

export class SentinelSecretsManagerClient implements SecretsManagerClientApi {
  private readonly fetchApi: FetchApi;
  private readonly discoveryApi: DiscoveryApi;

  constructor(options: SecretsManagerClientOptions) {
    this.fetchApi = options.fetchApi;
    this.discoveryApi = options.discoveryApi;
  }

  private filterRdcTags(tags: Record<string, string> | undefined): Record<string, string> | null {
    if (!tags || typeof tags !== 'object') {
      return null;
    }

    const filteredTags: Record<string, string> = {};
    Object.entries(tags).forEach(([key, value]) => {
      if (key.startsWith('rdc:') && typeof value === 'string') {
        filteredTags[key] = value;
      }
    });

    return Object.keys(filteredTags).length > 0 ? filteredTags : null;
  }

  private extractPreviousVersion(versionsResult: any): string {
    if (versionsResult?.data?.versions && Array.isArray(versionsResult.data.versions)) {
      const previousVersion = versionsResult.data.versions.find(
        (version: { version_stages: string[]; version_id: string }) =>
          version?.version_stages &&
          Array.isArray(version.version_stages) &&
          version.version_stages.includes('AWSPREVIOUS') &&
          version.version_id,
      );
      return previousVersion?.version_id ?? '';
    }
    return '';
  }

  private async handleSuccessResponse(result: Response, versionsResult: Response): Promise<SecretsBodyResponse> {
    const responseBody = await result.json();
    responseBody.data.tags = this.filterRdcTags(responseBody?.data?.tags);

    if (versionsResult.status === 200) {
      const secretVersionsResult = await versionsResult.json();
      responseBody.previous_version = this.extractPreviousVersion(secretVersionsResult);
    } else {
      responseBody.previous_version = '';
    }

    return responseBody as SecretsBodyResponse;
  }

  private async handleUnauthorizedResponse(result: Response, env: string): Promise<SecretsBodyResponse> {
    const responseBody = await result.json();
    return {
      status: responseBody.status,
      message: responseBody.message,
      arn: responseBody.arn,
      request_id: responseBody.request_id,
      env,
      data: {
        access_type: null,
        secrets: null,
      },
    } as SecretsBodyResponse;
  }

  public async getSecrets(projectSlug: string, env: string): Promise<SecretsBodyResponse | undefined> {
    const baseUrl = `${await this.discoveryApi.getBaseUrl('proxy')}/sentinel-api`;
    const [organization, repository] = projectSlug.split('/');
    const queryParams = new URLSearchParams({
      organization,
      repository,
      env,
    });

    const [result, versionsResult] = await Promise.all([
      this.fetchApi.fetch(`${baseUrl}?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Sender: 'devportal',
        },
      }),
      this.fetchApi.fetch(`${baseUrl}/versions?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Sender: 'devportal',
        },
      }),
    ]);

    if (result.status === 200) {
      return this.handleSuccessResponse(result, versionsResult);
    }

    if (result.status === 403 || result.status === 401) {
      return this.handleUnauthorizedResponse(result, env);
    }

    if (result.status === 404) {
      // Not found, return undefined
      // This will avoid showing a card container in the UI
      return undefined;
    }

    // This will show an error message in the UI
    throw new Error(`${result.status} ${result.statusText}, ${await result.text()}`);
  }

  public async updateSecrets(projectSlug: string, body: UpdateSecretsBody): Promise<UpdateSecretsResponse> {
    const [organization, repository] = projectSlug.split('/');
    const baseUrl = `${await this.discoveryApi.getBaseUrl('proxy')}/sentinel-api`;
    const result = await this.fetchApi.fetch(baseUrl, {
      method: 'POST',
      body: JSON.stringify({
        organization,
        repository,
        ...body,
      }),
      headers: {
        'Content-Type': 'application/json',
        Sender: 'devportal',
      },
    });

    if (result.status === 200 || result.status === 201) {
      const responseBody = await result.json();
      return responseBody as UpdateSecretsResponse;
    }

    throw new Error(`${result.status} ${result.statusText}, ${await result.text()}`);
  }

  public async revertSecret(projectSlug: string, body: RevertSecretVersionBody): Promise<UpdateSecretsResponse> {
    const baseUrl = `${await this.discoveryApi.getBaseUrl('proxy')}/sentinel-api`;
    const [organization, repository] = projectSlug.split('/');
    const result = await this.fetchApi.fetch(`${baseUrl}/versions`, {
      method: 'POST',
      body: JSON.stringify({
        organization,
        repository,
        ...body,
      }),
      headers: {
        'Content-Type': 'application/json',
        Sender: 'devportal',
      },
    });

    if (result.status === 200 || result.status === 201) {
      const responseBody = await result.json();
      return responseBody as UpdateSecretsResponse;
    }

    throw new Error(`${result.status} ${result.statusText}, ${await result.text()}`);
  }
}
